import React, { useEffect, useState, useCallback, useMemo, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { AnimatePresence } from "motion/react";
import * as motion from "motion/react-client";
import { toastr } from "react-redux-toastr";
import { Dimmer, Loader } from "semantic-ui-react";
import { Button, Select } from "antd";
import InfiniteScroll from "react-infinite-scroll-component";

// Стили и компоненты
import './styles.css';
import PhoneCountryMask from '../../components/AddressCard/PhoneCountryMask';
import EditPopup from '../../components/EditPopup';
import CreatePopup from '../../components/CreatePopup';
import DropdownComponent from "../../components/DropdownComponent/index";
import { AddressFieldsComponent, ContactFieldsComponent, CompanyFieldsComponent, SegmentationFieldsComponent} from "./ComponentFields";
import EventsTableComponent from '../../components/EventsTableComponent';

// Reducers and actions
import { getAuthToken } from "../../components/UserLogin/reducer";
import { fetchCounters, createCounter, changeCounter } from "../Counterparties/actions";
import { getCounters, getSingleCounter, getCountersFetching } from "../Counterparties/reducer";
import { changeEvent, deleteEvent, EVENTS_PAGE_COUNT, fetchEvents } from "../Events/actions";
import { getEvents, getEventsFetching, getEventsHasMore, getSingleEvent } from "../Events/reducer";
import { getLanguage } from "../../components/LanguageModal/reducer";
import translations from "../../components/LanguageModal/translations";
import { fetchUserData } from "../../components/UserLogin/actions";
import { getRolesAccess } from "../../components/Roles/Roles";
import { getAddressFields, getCountries, isCountriesFetching } from '../../components/AddressCard/reducer';
import { ADDRESS_PHONE, ADDRESS_PHONE_COUNTRY, setAddressFields } from '../../components/AddressCard/actions';

// Хуки и утилиты
const useTranslation = () => {
    const language = useSelector(getLanguage);
    const languageString = useMemo(() =>
            language[0] + language[1],
        [language]
    );

    const t = useCallback((key) => {
        const translation = translations?.[languageString];
        return translation?.[key] || key;
    }, [languageString]);

    return { t, languageString };
};

const useFormData = (initialData = {}) => {
    const [formData, setFormData] = useState({
        company: '',
        company_ogrn: '',
        company_inn: '',
        company_kpp: '',
        company_head: '',
        country: '',
        county: '',
        city: '',
        mail_index: '',
        address: '',
        address_more: '',
        first_name: '',
        last_name: '',
        phone_country: '',
        phone: '',
        email: '',
        website: '',
        segments: [],
        industries: [],
        administrator: '',
        manager: '',
        job_title: '',
        ...initialData
    });

    const updateField = useCallback((name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);

    const updateMultipleFields = useCallback((fields) => {
        setFormData(prev => ({ ...prev, ...fields }));
    }, []);

    const resetForm = useCallback(() => {
        setFormData({
            company: '',
            company_ogrn: '',
            company_inn: '',
            company_kpp: '',
            company_head: '',
            country: '',
            county: '',
            city: '',
            mail_index: '',
            address: '',
            address_more: '',
            first_name: '',
            last_name: '',
            phone_country: '',
            phone: '',
            email: '',
            website: '',
            segments: [],
            industries: [],
            administrator: '',
            manager: '',
            job_title: ''
        });
    }, []);

    return { formData, updateField, updateMultipleFields, resetForm, setFormData };
};

// Константы
const INITIAL_POPUP_DATA_CREATE = {
    name: "",
    on_date: "",
    on_time: "",
    duration: "",
    respondent_user: 0,
    event_action: "",
    placement: "",
    add_info: "",
};

const INITIAL_POPUP_DATA_EDIT = {
    add_info: '',
    on_date: '',
    on_time: '',
    event_status: '',
    event_action: '',
    respondent_user: '',
    duration: '',
    placement: '',
};

const MEETING_TYPES = [
    { textRu: "Телефонный звонок", textEn: "Phone Call", value: 'phone' },
    { textRu: "Электронное письмо", textEn: "Send Mail", value: 'mail' },
    { textRu: "Встреча онлайн", textEn: "Meeting Online", value: 'meet online' },
    { textRu: "Встреча оффлайн", textEn: "Meeting Offline", value: 'meet offline' },
    { textRu: "Входящее обращение", textEn: "Incoming Appeal", value: 'appeal' },
];

const ISSUES_OPTIONS = [
    { label: "Отказ", key: "discard" },
    { label: "Заинтересовало", key: "interest" },
    { label: "Нужно время подумать", key: "repeat" },
    { label: "Потенциально возможно", key: "maybe" },
    { label: "Предложение принято", key: "submit" }
];

export default function CounterForm() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {counterId} = useParams();
    const {t, languageString} = useTranslation();
    const {formData, updateField, updateMultipleFields, resetForm} = useFormData();

    // Selectors
    const token = useSelector(state => getAuthToken(state.userLogin));
    const counter = counterId ? useSelector(state => getSingleCounter(state.counters)) : null;
    const isLoadingCounter = useSelector(state => getCountersFetching(state.counters));
    const events = counterId ? useSelector(state => getEvents(state.events)) : [];
    const singleEvent = counterId ? useSelector(state => getSingleEvent(state.events)) : null;
    const hasMoreEvents = counterId ? useSelector(state => getEventsHasMore(state.events)) : false;
    const isLoadingEvents = useSelector(state => getEventsFetching(state.events));
    const userData = useSelector(state => state.userLogin.userData);
    const countries = useSelector(state => getCountries(state.address));
    const isFetching = useSelector(state => isCountriesFetching(state.address));
    const fields = useSelector(state => getAddressFields(state.address));

    // States
    const [editingBlock, setEditingBlock] = useState(null);
    const [checkboxIsJuridic, setCheckboxIsJuridic] = useState(true);
    const [isPopupCreateOpen, setIsPopupCreateOpen] = useState(false);
    const [isPopupEditOpen, setIsPopupEditOpen] = useState(false);
    const [popupDataCreate, setPopupDataCreate] = useState(INITIAL_POPUP_DATA_CREATE);
    const [popupDataEdit, setPopupDataEdit] = useState(INITIAL_POPUP_DATA_EDIT);
    const [regionList, setRegionList] = useState([]);

    // Состояние для ключей Dadata компонентов
    const [dadataKeys, setDadataKeys] = useState({
        company: 0,
        companyHead: 0,
        city: 0,
        address: 0
    });

    // Для акордионов в мобильной верссии
    const [mobileSegments, setMobileSegments] = useState({
        counter: true,
        contact: false,
        segmentation: false,
        events: false,
    });

    // Проверка доступа
    const roleAccess = useMemo(() => getRolesAccess(userData?.roles), [userData?.roles]);

    if (!token) {
        return <strong style={{margin: '0 0 0 50px'}}>Для получения доступа к списку, пожалуйста,
            авторизуйтесь</strong>;
    }

    // Функция для обновления ключей (принудительное обновление компонентов)
    const updateDadataKeys = useCallback(() => {
        setDadataKeys(prev => ({
            company: prev.company + 1,
            companyHead: prev.companyHead + 1,
            city: prev.city + 1,
            address: prev.address + 1
        }));
    }, []);

    // Memoized options
    const typeMeetingOptions = useMemo(() =>
            MEETING_TYPES.map(type => ({
                label: languageString === 'ru' ? type.textRu : type.textEn,
                value: type.value
            })),
        [languageString]
    );

    const usersOptions = useMemo(() =>
            counter?.users?.map(user => ({
                text: languageString === 'ru' ? user.full_name : user.full_name_en,
                value: String(user.id),
            })) || [],
        [counter?.users, languageString]
    );

    const eventStatusesOptions = useMemo(() =>
            singleEvent?.settings?.crm_event_statuses
                ? Object.entries(singleEvent.settings.crm_event_statuses).map(([value, texts]) => ({
                    label: texts[languageString] || value,
                    value,
                }))
                : [],
        [singleEvent?.settings?.crm_event_statuses, languageString]
    );

    const issuesOptions = useMemo(() =>
            singleEvent?.settings?.crm_event_issues
                ? Object.entries(singleEvent.settings.crm_event_issues).map(([value, texts]) => ({
                    label: texts[languageString] || value,
                    value,
                }))
                : [],
        [singleEvent?.settings?.crm_event_issues, languageString]
    );

    const segmentsOptions = useMemo(() => {
        const segmentsList = userData?.options?.segments || {};
        return Object.keys(segmentsList).map(key => ({
            text: segmentsList[key],
            value: key
        }));
    }, [userData?.options?.segments]);

    const industriesOptions = useMemo(() => {
        const industriesList = userData?.options?.industries || {};
        return Object.keys(industriesList).map(key => ({
            text: industriesList[key],
            value: key
        }));
    }, [userData?.options?.industries]);

    const arrayCountries = useMemo(() => {
        if (!countries?.countries) return [];
        return Object.values(countries.countries).map(country => ({
            key: country.iso.toLowerCase(),
            value: country.iso.toLowerCase(),
            flag: country.iso.toLowerCase(),
            text: languageString === "en" ? country.country_en : country.country_ru,
            states: country.states,
            emoji: country.emoji,
        }));
    }, [countries?.countries, languageString]);

    const listPhoneCountries = useMemo(() => {
        if (isFetching || !countries?.countries) return [];
        return Object.values(countries.countries).map((element, index) => ({
            key: index,
            text: languageString === "en" ? element.country_en : element.country_ru,
            flag: element.iso.toLowerCase(),
            value: element.iso,
            className: "dropdown-item",
            phone_mask: element.phone_mask,
        }));
    }, [countries?.countries, languageString, isFetching]);

    const phoneMask = useMemo(() =>
            listPhoneCountries.find(item => item.value === formData.phone_country)?.phone_mask,
        [listPhoneCountries, formData.phone_country]
    );

    const isLoading = isLoadingEvents || isLoadingCounter;

    // Callbacks
    const changeRegion = useCallback((value) => {
        console.log(value);
        const searchCountry = arrayCountries.find(country => country.value === value.toLowerCase());
        const regions = searchCountry?.states
            ? Object.values(searchCountry.states).map(region => ({
                text: region.name,
                value: region.code.toString()
            }))
            : [];
        setRegionList(regions);
    }, [arrayCountries]);

    const readEvents = useCallback((page) => {
        if (!counterId) return;
        dispatch(fetchEvents(token, {
            page,
            per_page: EVENTS_PAGE_COUNT,
            order: 'asc',
            orderby: 'on_date',
            ca_id: counterId,
            lang: languageString,
        }, t));
    }, [dispatch, token, counterId, languageString, t]);

    const loadEvents = useCallback(() => {
        if (hasMoreEvents) {
            const nextPage = Math.round(events.length / EVENTS_PAGE_COUNT) + 1;
            readEvents(nextPage);
        }
    }, [hasMoreEvents, events.length, readEvents]);

    const handleDoneEvent = useCallback((value, id, addInfo) => {
        dispatch(changeEvent(token, id, {
            add_info: addInfo,
            event_status: 'done',
            issue: value,
            lang: languageString,
        }, t)).then(() => {
            readEvents(1);
        }).catch((error) => {
            console.error("Ошибка при обновлении события:", error);
        });
    }, [dispatch, token, languageString, t, readEvents]);

    const handleDeleteEvent = useCallback((id) => {
        dispatch(deleteEvent(token, id, {lang: languageString}, t));
        readEvents(1);
    }, [dispatch, token, languageString, t, readEvents]);

    const handleEditEvent = useCallback((id, fields) => {
        dispatch(changeEvent(token, id, {...fields, lang: languageString}, t));
        readEvents(1);
    }, [dispatch, token, languageString, t, readEvents]);

    const handleRowClick = useCallback((data) => {
        dispatch(fetchEvents(token, {id: data.id})).then(() => {
            setIsPopupEditOpen(true);
        });
    }, [dispatch, token]);

    const handleCancelEvent = useCallback((id, addInfo) => {
        dispatch(changeEvent(token, id, {
            add_info: addInfo,
            event_status: 'canceled',
            issue: 'none',
            lang: languageString,
        }, t)).then(() => {
            readEvents(1);
        }).catch((error) => {
            console.error("Ошибка при обновлении события:", error);
        });
    }, [dispatch, token, languageString, t, readEvents]);

    const handleDelayedEvent = useCallback((id, addInfo) => {
        dispatch(changeEvent(token, id, {
            add_info: addInfo,
            event_status: 'delayed',
            issue: 'none',
            lang: languageString,
        }, t)).then(() => {
            readEvents(1);
        }).catch((error) => {
            console.error("Ошибка при обновлении события:", error);
        });
    }, [dispatch, token, languageString, t, readEvents]);


    const setDirectField = useCallback((name, data) => {
        console.log('setDirectField вызван:', name, data);

        if (name === 'countryPhoneCode') {
            // Очищаем телефон при изменении кода страны
            updateField('phone', '');
            updateField('phone_country', data);

            // Также обновляем fields для PhoneCountryMask
            const updatedFields = {...fields};
            updatedFields[ADDRESS_PHONE] = '';
            updatedFields[ADDRESS_PHONE_COUNTRY] = data;
            dispatch(setAddressFields(updatedFields));
        } else if (name === ADDRESS_PHONE) {
            // Обновляем номер телефона
            updateField('phone', data);

            // Также обновляем fields
            const updatedFields = {...fields};
            updatedFields[ADDRESS_PHONE] = data;
            dispatch(setAddressFields(updatedFields));
        } else if (name === ADDRESS_PHONE_COUNTRY) {
            // Обновляем код страны
            updateField('phone_country', data);

            // Также обновляем fields
            const updatedFields = {...fields};
            updatedFields[ADDRESS_PHONE_COUNTRY] = data;
            dispatch(setAddressFields(updatedFields));
        } else {
            // Для других полей
            const updatedFields = {...fields};
            updatedFields[name] = data;
            dispatch(setAddressFields(updatedFields));
        }
    }, [fields, dispatch, updateField]);

    const handleChange = useCallback((e) => {
        const {name, value} = e.target;
        updateField(name, value);
    }, [updateField]);

    const handleChangePopupCreate = useCallback((e) => {
        const {name, value} = e.target;
        setPopupDataCreate(prev => ({...prev, [name]: value}));
    }, []);

    const handleChangePopupEdit = useCallback((e) => {
        const {name, value} = e.target;
        setPopupDataEdit(prev => ({...prev, [name]: value}));
    }, []);

    // Обработчики dropdown (теперь select)
    const handleChangeCountry = useCallback((value) => {
        console.log(value);
        updateField('country', value);
        changeRegion(value);
    }, [updateField, changeRegion]);

    const handleChangeCounty = useCallback((value) => {
        updateField('county', value);
    }, [updateField]);

    const handleChangePersonPopup = useCallback((value) => {
        setPopupDataCreate(prev => ({...prev, respondent_user: value}));
    }, []);

    const handleChangeEventPopup = useCallback((value) => {
        setPopupDataCreate(prev => ({...prev, event_action: value}));
    }, []);

    // Dadata handlers
    const handleChangeFirstName = useCallback((value) => {
        updateField('first_name', value.value);
    }, [updateField]);

    const handleChangeLastName = useCallback((value) => {
        updateField('last_name', value.value);
    }, [updateField]);

    const handleChangeCompanyHead = useCallback((value) => {
        updateField('company_head', value.value);
    }, [updateField]);

    const handleChangeCompanyName = useCallback((value) => {
        updateMultipleFields({
            company: value.value,
            company_kpp: value.data.kpp || '',
            company_inn: value.data.inn || '',
            company_ogrn: value.data.ogrn || '',
            company_head: value.data.management.name || '',
            country: value.data.address?.data?.country_iso_code?.toLowerCase() || '',
            county: regionList.find(item =>
                item.text === (value.data.address?.data?.region + ' ' + value.data.address?.data?.region_type_full)
            )?.value || '',
            city: value.data.address?.data?.city_with_type || '',
            mail_index: value.data.address?.data?.postal_code || '',
            address: value.data.address?.value || '',
        });
        if (value.data.address?.data?.country_iso_code) {
            changeRegion(value.data.address.data.country_iso_code.toLowerCase());
        }

        // Обновляем ключи для принудительного обновления Dadata компонентов
        setTimeout(() => {
            updateDadataKeys();
        }, 50);
    }, [updateMultipleFields, regionList, changeRegion, updateDadataKeys]);

    const handleChangeAddress = useCallback((value) => {
        updateField('address', value.value);
    }, [updateField]);

    const handleChangeEmail = useCallback((value) => {
        updateField('email', value.value);
    }, [updateField]);

    const handleChangeCity = useCallback((value) => {
        updateField('city', value.value);
    }, [updateField]);

    // CRUD operations
    const handleCreateCounter = useCallback(() => {
        dispatch(createCounter(token, {
            country: formData.country.toUpperCase(),
            [languageString==='ru' ? 'name' : 'name_en']: checkboxIsJuridic ? formData.company : formData.first_name + ' ' + formData.last_name,

            url: formData.website,
            billing: {
                first_name: formData.first_name,
                last_name: formData.last_name,
                phone_country: formData.phone_country.toUpperCase(),
                company_job_title: formData.job_title,
                company_chief_name: formData.company_head,
                company_ogrn: formData.company_ogrn,
                company_kpp: formData.company_kpp,
                company_inn: formData.company_inn,
                phone: formData.phone,
                email: formData.email,
                address_1: formData.address,
                address_2: formData.address_more,
                city: formData.city,
                state: formData.county,
                postcode: formData.mail_index,
                company: formData.company,
                is_juridic: checkboxIsJuridic,
            },
            lang: languageString,
        }, t)).then((res) => {
            navigate(`../counterparty/${res.id}`);
        });
    }, [dispatch, token, formData, checkboxIsJuridic, languageString, t, navigate]);

    const changeCounterFieldsJuridic = useCallback(() => {
        const billingKey = languageString === 'ru' ? 'billing' : 'billing_en';
        const payload = {
            country: formData.country.toUpperCase(),
            [languageString==='ru' ? 'name' : 'name_en']: formData.company,
            [billingKey]: {
                company_chief_name: formData.company_head,
                company_ogrn: formData.company_ogrn,
                company_kpp: formData.company_kpp,
                company_inn: formData.company_inn,
                state: formData.county,
                city: formData.city,
                postcode: formData.mail_index,
                address_1: formData.address,
                address_2: formData.address_more,
                first_name: formData.first_name,
                last_name: formData.last_name,
                company_job_title: formData.job_title,
                phone_country: formData.phone_country.toUpperCase(),
                phone: formData.phone,
                email: formData.email,
                company: formData.company,
                is_juridic: checkboxIsJuridic,
            },
            lang: languageString,
        };

        dispatch(changeCounter(token, counterId, payload, t)).then(() => {
            dispatch(fetchCounters(token, { id: counterId, lang: languageString }, t));
        });
    }, [dispatch, token, counterId, formData, checkboxIsJuridic, languageString, t]);



    const changeCounterFieldsPerson = useCallback(() => {
        const billingKey = languageString === 'ru' ? 'billing' : 'billing_en';
        dispatch(changeCounter(token, counterId, {
            country: formData.country.toUpperCase(),
            url: formData.website,
            [billingKey]: {
                first_name: formData.first_name,
                last_name: formData.last_name,
                phone_country: formData.phone_country.toUpperCase(),
                phone: formData.phone,
                state: formData.county,
                city: formData.city,
                postcode: formData.mail_index,
                address_1: formData.address,
                address_2: formData.address_more,
                email: formData.email,
                is_juridic: checkboxIsJuridic,
            },
            lang: languageString,
        }, t));
        dispatch(fetchCounters(token, {id: counterId, lang: languageString}, t));
    }, [dispatch, token, counterId, formData, checkboxIsJuridic, languageString, t]);

    const changeSegmentationFields = useCallback(() => {
        const billingKey = languageString === 'ru' ? 'billing' : 'billing_en';
        dispatch(changeCounter(token, counterId, {
            crm_manager: formData.manager,
            crm_admin: formData.administrator,
            segments: formData.segments,
            industries: formData.industries,
            [billingKey]: {
                is_juridic: checkboxIsJuridic,
            },
            lang: languageString,
        }, t));
        dispatch(fetchCounters(token, {id: counterId, lang: languageString}, t));
    }, [dispatch, token, counterId, formData, checkboxIsJuridic, languageString, t]);

    const processRegion = useCallback((regionText) => {
        let regionType = null;
        let cleanRegionName = regionText;

        if (regionText.includes('область')) {
            regionType = 'обл';
            cleanRegionName = regionText.replace(' область', '').replace('автономная ', '');
        } else if (regionText.includes('край')) {
            regionType = 'край';
            cleanRegionName = regionText.replace(' край', '');
        } else if (regionText.includes('Республика') || ['Адыгея', 'Башкортостан', 'Бурятия', 'Дагестан', 'Ингушетия', 'Калмыкия', 'Карелия', 'Коми', 'Крым', 'Марий Эл', 'Мордовия', 'Северная Осетия', 'Татарстан', 'Тыва', 'Удмуртия', 'Хакасия', 'Чечня', 'Чувашия', 'Якутия'].includes(regionText)) {
            regionType = 'респ';
            cleanRegionName = regionText.replace(', Республика', '').replace(' народная республика', '');
        } else if (regionText.includes('автономный округ')) {
            regionType = 'округ';
            cleanRegionName = regionText.replace(' автономный округ', '').replace(' - Югра', '');
        } else if (['Москва', 'Санкт-Петербург', 'Севастополь'].includes(regionText)) {
            regionType = 'г';
        }

        cleanRegionName = cleanRegionName.split(',')[0].trim();
        return {regionName: cleanRegionName, regionType};
    }, []);

    const extractCityName = useCallback((cityText) => {
        if (!cityText) return null;
        const parts = cityText.split(',').map(part => part.trim());
        let cityName = parts[parts.length - 1];
        cityName = cityName.replace(/^(г\.?|город)\s+/i, '');
        return parts.length === 1 ? cityName.trim() : cityName.trim();
    }, []);

    const selectedRegion = useMemo(() =>
            regionList.find(item => item.value === formData.county),
        [regionList, formData.county]
    );

    const regionData = useMemo(() =>
            selectedRegion ? processRegion(selectedRegion.text) : null,
        [selectedRegion, processRegion]
    );

    const cleanCityName = useMemo(() =>
            extractCityName(formData.city),
        [formData.city, extractCityName]
    );

    const handleEditBlock = useCallback((block) => {
        if (editingBlock === null) {
            setEditingBlock(block);
        } else if (editingBlock === block) {
            if (block === 'counter') {
                checkboxIsJuridic ? changeCounterFieldsJuridic() : changeCounterFieldsPerson();
            } else if (block === 'segment') {
                changeSegmentationFields();
            }
            setEditingBlock(null);
        } else {
            toastr.error(t('finishEditing'));
        }
    }, [editingBlock, checkboxIsJuridic, t, formData, changeCounterFieldsJuridic, changeCounterFieldsPerson, changeSegmentationFields]);

    // Effects
    useEffect(() => {
        if (token && counterId) {
            dispatch(fetchCounters(token, {id: counterId, lang: languageString}, t));
            readEvents(1);
        }
    }, [token, counterId, dispatch, languageString, t, readEvents]);

    useEffect(() => {
        if (fields && formData.phone !== fields[ADDRESS_PHONE]) {
            const updatedFields = {...fields};
            updatedFields[ADDRESS_PHONE] = formData.phone;
            updatedFields[ADDRESS_PHONE_COUNTRY] = formData.phone_country;
            dispatch(setAddressFields(updatedFields));
        }
    }, [formData.phone, formData.phone_country, fields, dispatch]);

    useEffect(() => {
        if (counter) {
            const billingData = languageString === 'ru' ? counter.billing : counter.billing_en;

            updateMultipleFields({
                company: billingData?.company || '',
                company_ogrn: billingData?.company_ogrn || '',
                company_inn: billingData?.company_inn || '',
                company_kpp: billingData?.company_kpp || '',
                company_head: billingData?.company_chief_name || '',
                country: counter.country?.toLowerCase() || '',
                county: billingData?.state || '',
                city: billingData?.city || '',
                mail_index: billingData?.postcode || '',
                address: billingData?.address_1 || '',
                address_more: billingData?.address_2 || '',
                first_name: billingData?.first_name || '',
                last_name: billingData?.last_name || '',
                job_title: billingData?.company_job_title || '',
                phone_country: billingData?.phone_country || '',
                phone: billingData?.phone || '',
                email: billingData?.email || '',
                website: counter.url || '',
                segments: counter.segments?.map(item => String(item.id)) || [],
                industries: counter.industries?.map(item => String(item.id)) || [],
                administrator: counter.crm_admin?.user_id || '',
                manager: counter.crm_manager?.user_id || '',
            });

            if (billingData?.country) {
                changeRegion(billingData.country.toLowerCase());
            }
            setCheckboxIsJuridic(
                billingData?.is_juridic !== undefined
                    ? (billingData.is_juridic === true || billingData.is_juridic === "true")
                    : true
            );

            // Обновляем ключи для принудительного обновления Dadata компонентов
            setTimeout(() => {
                updateDadataKeys();
            }, 50);
        }
    }, [counter, languageString, updateMultipleFields, changeRegion, updateDadataKeys]);

    useEffect(() => {
        if (!roleAccess.pageCounter || !token) {
            navigate('/');
            return;
        }
        window.scrollTo(0, 0);
        setPopupDataCreate(INITIAL_POPUP_DATA_CREATE);
        setPopupDataEdit(INITIAL_POPUP_DATA_EDIT);

        if (!counterId) {
            resetForm();
        }
    }, [roleAccess.pageCounter, token, navigate, counterId]);

    useEffect(() => {
        console.log('FormData updated:', formData);
    }, [formData]);

    useEffect(() => {
        if (singleEvent) {
            setPopupDataEdit({
                add_info: singleEvent.add_info || '',
                on_date: singleEvent.on_date || '',
                on_time: singleEvent.on_time || '',
                event_status: singleEvent.event_status?.code || '',
                event_action: singleEvent.event_action?.code || '',
                respondent_user: singleEvent.respondent_ca_user?.user_id || '',
                duration: singleEvent.duration || '',
                placement: singleEvent.placement || '',
            });
        }
    }, [singleEvent]);

    const renderCounterFields = () => {
        const isDisabled = counterId && editingBlock !== 'counter';
        return (
            <div>
                <div className='cform-field-header' style={{justifyContent: 'space-between'}}>
                    {!isMobile && <div className='cform-title-segment'>{t('aboutCounterparty')}</div>}
                    {counterId && !isMobile && (
                        <div
                            className={`cform-edit-btn ${editingBlock === 'counter' ? 'cform-edit-btn-gray' : ''}`}
                            onClick={() => handleEditBlock('counter')}
                        >
                            {editingBlock === 'counter' ? t('save') : t('edit')}
                        </div>
                    )}
                </div>
                <div className={!isMobile ? 'cform-grid' : ''}>
                    <div>
                        <CompanyFieldsComponent
                            isDisabled={isDisabled}
                            checkboxIsJuridic={checkboxIsJuridic}
                            formData={formData}
                            handleChange={handleChange}
                            handleChangeCompanyName={handleChangeCompanyName}
                            handleChangeCompanyHead={handleChangeCompanyHead}
                            handleChangeFirstName={handleChangeFirstName}
                            handleChangeLastName={handleChangeLastName}
                            setDirectField={setDirectField}
                            listPhoneCountries={listPhoneCountries}
                            phoneMask={phoneMask}
                            isFetching={isFetching}
                            t={t}
                            ADDRESS_PHONE_COUNTRY={ADDRESS_PHONE_COUNTRY}
                            ADDRESS_PHONE={ADDRESS_PHONE}
                            dadataKeys={dadataKeys}
                        />
                        <AddressFieldsComponent
                            isDisabled={isDisabled}
                            formData={formData}
                            handleChange={handleChange}
                            handleChangeCountry={handleChangeCountry}
                            handleChangeCounty={handleChangeCounty}
                            handleChangeCity={handleChangeCity}
                            handleChangeAddress={handleChangeAddress}
                            arrayCountries={arrayCountries}
                            regionList={regionList}
                            regionData={regionData}
                            selectedRegion={selectedRegion}
                            cleanCityName={cleanCityName}
                            t={t}
                            dadataKeys={dadataKeys}
                        />
                    </div>
                    <div>
                        <ContactFieldsComponent
                            isDisabled={isDisabled}
                            checkboxIsJuridic={checkboxIsJuridic}
                            formData={formData}
                            handleChange={handleChange}
                            handleChangeFirstName={handleChangeFirstName}
                            handleChangeLastName={handleChangeLastName}
                            handleChangeEmail={handleChangeEmail}
                            setDirectField={setDirectField}
                            listPhoneCountries={listPhoneCountries}
                            phoneMask={phoneMask}
                            isFetching={isFetching}
                            t={t}
                            ADDRESS_PHONE_COUNTRY={ADDRESS_PHONE_COUNTRY}
                            ADDRESS_PHONE={ADDRESS_PHONE}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const renderSegmentFields = () => {
        const isDisabled = counterId && editingBlock !== 'segment';
        return (
            <div>
                <div className='cform-field-header' style={{justifyContent: 'space-between'}}>
                    {!isMobile && <div className='cform-title-segment'>{t('segmentation')}</div>}
                    {counterId && !isMobile && (
                        <div
                            className={`cform-edit-btn ${editingBlock === 'segment' ? 'cform-edit-btn-gray' : ''}`}
                            onClick={() => handleEditBlock('segment')}
                        >
                            {editingBlock === 'segment' ? t('save') : t('edit')}
                        </div>
                    )}
                </div>
                <SegmentationFieldsComponent
                    isDisabled={isDisabled}
                    formData={formData}
                    updateField={updateField}
                    usersOptions={usersOptions}
                    industriesOptions={industriesOptions}
                    segmentsOptions={segmentsOptions}
                    t={t}
                />
            </div>
        );
    };

    const toggleMobileSegment = useCallback((segment) => {
        setMobileSegments(prev => ({
            ...prev,
            [segment]: !prev[segment]
        }));
    }, []);

    if (isLoading) {
        return (
            <Dimmer inverted active={isLoading}>
                <Loader size="large" active={isLoading}>{t('loadingCounter')}</Loader>
            </Dimmer>
        );
    }

    const renderCheckboxes = () => (
        <div className='cform-checkbox-ctn'>
            <div style={{display: 'flex', marginRight: '24px'}}>
                <input
                    className='cform-checkbox'
                    type='checkbox'
                    checked={checkboxIsJuridic}
                    onChange={() => setCheckboxIsJuridic(true)}
                />
                <div style={{fontSize: '15px'}}>{t('legalEntity')}</div>
            </div>
            <div style={{display: 'flex', marginRight: '24px'}}>
                <input
                    className='cform-checkbox'
                    type='checkbox'
                    checked={!checkboxIsJuridic}
                    onChange={() => setCheckboxIsJuridic(false)}
                />
                <div style={{fontSize: '15px'}}>{t('naturalPerson')}</div>
            </div>
        </div>
    );

    const renderMobileForm = () => (
        <div className='cform'>
            <div className='cform-title'>{counterId ? counter?.[languageString === 'en' ? 'billing_en' : 'billing']?.company : t('creatingCounter')}</div>
            {renderCheckboxes()}
            <form>
                <div style={{width: '100%'}}>
                    <div className='cform-segment-ctn'>
                        <div className='cform-title-segment'>
                            <div
                                style={{width: '50%'}}
                                onClick={() => toggleMobileSegment('counter')}
                            >
                                {t('aboutCounterparty')}{mobileSegments.counter ? '↓' : '↑'}
                            </div>
                            {mobileSegments.counter && counterId && (
                                <div
                                    className={`cform-edit-btn ${editingBlock === 'counter' ? 'cform-edit-btn-gray' : ''}`}
                                    onClick={() => handleEditBlock('counter')}
                                >
                                    {editingBlock === 'counter' ? t('save') : t('edit')}
                                </div>
                            )}
                        </div>
                        {mobileSegments.counter && (
                            <fieldset className='cform-segment'>
                                {renderCounterFields()}
                            </fieldset>
                        )}
                    </div>

                    {counterId && (
                        <div className='cform-segment-ctn'>
                            <div className='cform-title-segment'>
                                <div
                                    style={{width: '50%'}}
                                    onClick={() => toggleMobileSegment('segmentation')}
                                >
                                    {t('segmentation')}{mobileSegments.segmentation ? '↓' : '↑'}
                                </div>
                                {mobileSegments.segmentation && (
                                    <div
                                        className={`cform-edit-btn ${editingBlock === 'segment' ? 'cform-edit-btn-gray' : ''}`}
                                        onClick={() => handleEditBlock('segment')}
                                    >
                                        {editingBlock === 'segment' ? t('save') : t('edit')}
                                    </div>
                                )}
                            </div>
                            {mobileSegments.segmentation && (
                                <fieldset className='cform-segment'>
                                    {renderSegmentFields()}
                                </fieldset>
                            )}
                        </div>
                    )}
                </div>
            </form>
            {counterId && (
                <div className='cform-segment-ctn'>
                    <div className='cform-title-segment'>
                        <div onClick={() => toggleMobileSegment('events')}>
                            {t('events')}{mobileSegments.events ? '↓' : '↑'}
                        </div>
                        {mobileSegments.events && (
                            <CreatePopup
                                isPopupOpen={isPopupCreateOpen}
                                setIsPopupOpen={setIsPopupCreateOpen}
                                options={{
                                    typeMeetingOptions,
                                    usersOptions,
                                }}
                                inForm={true}
                                counterId={counterId}
                                currentCounter={counter}
                            />
                        )}
                    </div>
                    {mobileSegments.events && (
                        <div className='cform-segment'>
                            <EventsTableComponent
                                events={events}
                                languageString={languageString}
                                handleDoneEvent={handleDoneEvent}
                                handleDeleteEvent={handleDeleteEvent}
                                handleRowClick={handleRowClick}
                                loadEvents={loadEvents}
                                hasMoreEvents={hasMoreEvents}
                                t={t}
                                isLoading={isLoadingEvents}
                            />
                        </div>
                    )}
                </div>
            )}
            {!counterId && (
                <div className='cform-create-btn' onClick={handleCreateCounter}>
                    {t('create')}
                </div>
            )}
        </div>
    );

    const renderDesktopForm = () => (
        <div className='cform'>
            <div className='cform-title'>{counterId ? counter?.[languageString === 'en' ? 'billing_en' : 'billing']?.company : t('creatingCounter')}</div>
            {renderCheckboxes()}
            <div>
                {renderCounterFields()}
            </div>
            <div style={{marginTop: '15px'}}>
                {counterId && renderSegmentFields()}
            </div>
            {counterId && (
                <div style={{marginTop: '25px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '15px'}}>
                        <div className='cform-title-segment'>{t('events')}</div>
                        <CreatePopup
                            isPopupOpen={isPopupCreateOpen}
                            setIsPopupOpen={setIsPopupCreateOpen}
                            options={{
                                typeMeetingOptions,
                                usersOptions,
                            }}
                            inForm={true}
                            counterId={counterId}
                            currentCounter={counter}
                        />
                    </div>
                    <EventsTableComponent
                        events={events}
                        languageString={languageString}
                        handleDoneEvent={handleDoneEvent}
                        handleDeleteEvent={handleDeleteEvent}
                        handleRowClick={handleRowClick}
                        loadEvents={loadEvents}
                        hasMoreEvents={hasMoreEvents}
                        t={t}
                        isLoading={isLoadingEvents}
                    />
                </div>
            )}
            {!counterId && (
                <div className='cform-create-btn' onClick={handleCreateCounter}>
                    {t('create')}
                </div>
            )}
        </div>
    );

    return (
        <>
            {isMobile ? renderMobileForm() : renderDesktopForm()}
            <EditPopup
                isPopupOpen={isPopupEditOpen}
                setIsPopupOpen={setIsPopupEditOpen}
                isLoading={isLoadingEvents}
                event={singleEvent}
                deleteFunction={handleDeleteEvent}
                editFunction={handleEditEvent}
                options={{
                    eventStatusesOptions,
                    typeMeetingOptions,
                    usersOptions,
                    issuesOptions,
                }}
            />
        </>
    )
}